/**
 * R-5 — the root belongs to namespaces; everything internal lives under `/-/` (F-0250).
 *
 * Jay, 2026-09-26: _"I think a / is better between handle and workarea"_ and _"first level is always
 * the handle"_. So:
 *
 * ```
 * /acme                              a namespace
 * /acme/tracker                      a work area
 * /acme/platform/api                 work area `api` in subgroup `acme/platform`
 * /acme/tracker/-/items/F-0160       a route on that work area
 * /-/settings                        an app route
 * /login                             an entry door, which never moves (RA-22)
 * ```
 *
 * ⭐ **This file is the one place an address is built or read.** `appPath` and `workAreaRoutePath`
 * build, `parseAddress` reads, and the web router, every link helper, the backend's links, the MCP
 * server, the push payload and the iPhone app's path configuration all go through them. A guard
 * (`hand-built-addresses.guard.spec.ts`, AC-AD8) fails on an address written anywhere else.
 *
 * The grammar is decided by the first segment and by the `-` separator, never by a word list: `-`
 * cannot be a namespace handle or a slug (AC-AD14), so the first `-` segment always ends the work
 * area's own path, at any depth.
 */

/** The segment that separates a work area's own path from its routes, and holds every app route. */
export const INTERNAL_ROUTE_SEGMENT = '-';

/** Where every app route lives: `/-/settings`. */
export const INTERNAL_PREFIX = `/${INTERNAL_ROUTE_SEGMENT}`;

/**
 * How many namespace levels an address may carry: a root namespace and nine subgroup levels.
 *
 * ⚠️ Routes are declared once per depth (`workAreaAddressPatterns`), because the web router and the
 * iPhone app's matcher both match fixed-length patterns — and an app already installed reads the
 * served patterns with the matcher it shipped with, so a new wildcard syntax would be misread in the
 * field. Subgroup creation refuses an eleventh level (AC-AD3), so no work area is ever unaddressable.
 * GitLab's cap is twenty; this one keeps the served path configuration small. One constant raises it.
 */
export const MAX_NAMESPACE_DEPTH = 10;

/**
 * The root segments the outside world addresses, which therefore never move (RA-22) and are
 * reserved so no namespace can take them (R-7, AC-AD7).
 *
 * | segment | who holds it |
 * |---|---|
 * | `verify-email`, `reset-password` | inboxes: the backend builds them into sent mail |
 * | `cli-auth` | installed CLI binaries build it themselves |
 * | `desktop-auth` | installed desktop apps build it themselves (F-0819) |
 * | `mobile-auth` | installed phone apps build it themselves (F-0228) |
 * | `passkey-ceremony` | installed desktop apps build it themselves (I-0775) |
 * | `app-setup` | setup links, QR codes and universal links (F-0237) |
 * | `connector-consent` | `/oauth/authorize` redirects here (F-0219) |
 * | `console-sign-in` | the admin console's Sign in with FEATit redirects here (F-0616 S2) |
 * | `unsubscribe` | the one-click unsubscribe link lands here (I-0637) |
 * | `confirm-sign-in-address`, `undo-sign-in-address` | the two links a sign-in address change mails (F-0557) |
 * | `setup`, `claim` | the one-time links an operator or an agent hands out (I-0638) |
 * | `connect`, `login`, `register`, `verify-session`, `forgot-password` | the screens a person reaches before any session exists |
 * | `welcome` | the desktop app's first screen, Create account and Sign in (F-0518) |
 *
 * ⚠️ `setup`, `claim`, `unsubscribe` and the pre-account screens are a judgement, flagged in the
 * concept (§10.5, decision 3): moving one under `/-/` is removing it here.
 */
export const ROOT_ENTRY_SEGMENTS: readonly string[] = [
  'connect',
  'login',
  'welcome',
  // F-0581 OB-1: Get started, a first start's way in.
  'get-started',
  'register',
  'cli-auth',
  'desktop-auth',
  'connector-consent',
  'console-sign-in',
  'mobile-auth',
  'passkey-ceremony',
  'app-setup',
  'verify-session',
  'verify-email',
  'forgot-password',
  'reset-password',
  'setup',
  'claim',
  'unsubscribe',
  'confirm-sign-in-address',
  'undo-sign-in-address',
];

/**
 * Root segments the deployment's edge answers before the app sees the request (`web/nginx.stable.conf`,
 * `infrastructure/router/nginx.conf`, the web's public files). A namespace called `mcp` would have an
 * address the edge sends to the backend, so each of these is reserved (AC-AD7).
 */
export const EDGE_ROOT_SEGMENTS: readonly string[] = [
  'api',
  'mcp',
  'oauth',
  '.well-known',
  'shell',
  'assets',
];

/** The work-area segment of the interim address (`/p/<namespace>~<slug>`, AC-M19), now replaced. */
export const LEGACY_WORK_AREA_SEGMENT = 'p';

/**
 * The app routes' root segments before the cutover (`/settings`, `/items/F-1`). Each is replaced by
 * its `/-/` address (AC-AD6) and stays reserved (AC-AD7), so a desktop tab or a link saved before the
 * cutover opens where it always did and nobody can take the old address.
 */
export const LEGACY_APP_SEGMENTS: readonly string[] = [
  'settings',
  'admin',
  'profile',
  'more',
  'agents',
  'infrastructure',
  'work-areas',
  'home',
  'roadmap',
  'activity',
  'views',
  'documents',
  'items',
  'decisions',
  'new',
];

/**
 * Whether a work area may be called `slug` as far as its address goes (AC-AD14). `-` is the separator
 * between a work area's own path and its routes, so a work area slugged `-` could never be opened.
 */
export function isAddressableWorkAreaSlug(slug: string): boolean {
  return slug !== INTERNAL_ROUTE_SEGMENT;
}

/** What a refused slug is told. */
export const UNADDRESSABLE_SLUG_MESSAGE = `A work area cannot be called "${INTERNAL_ROUTE_SEGMENT}": it separates a work area's address from its pages.`;

/** Splits a path at `?` or `#`, so a route may carry a query and a fragment through a builder. */
function splitSuffix(route: string): { path: string; suffix: string } {
  const at = route.search(/[?#]/);
  return at === -1
    ? { path: route, suffix: '' }
    : { path: route.slice(0, at), suffix: route.slice(at) };
}

/** The non-empty segments of a path. */
function segmentsOf(path: string): string[] {
  return path.split('/').filter((segment) => segment !== '');
}

/** A route given as a path (`/items/F-1?c=2`) or as raw segments (`['items', key]`, encoded here). */
export type RouteInputType = string | readonly string[];

function routeSuffixOf(route: RouteInputType): { body: string; suffix: string } {
  if (typeof route !== 'string') {
    const body = route
      .filter((segment) => segment !== '')
      .map(encodeURIComponent)
      .join('/');
    return { body, suffix: '' };
  }
  const { path, suffix } = splitSuffix(route);
  return { body: segmentsOf(path).join('/'), suffix };
}

/**
 * An app route: `appPath('/settings?section=email')` → `/-/settings?section=email`.
 *
 * Give it the route as it reads inside the app, with its query and fragment. `appPath('/')` is the
 * app's own root, `/-`. A route given as segments is encoded segment by segment.
 */
export function appPath(route: RouteInputType): string {
  const { body, suffix } = routeSuffixOf(route);
  return `${INTERNAL_PREFIX}${body ? `/${body}` : ''}${suffix}`;
}

/**
 * The address of a work area, or of a route on it.
 *
 * `reference` is the work area's route reference, `acme/platform/api` (`workAreaRouteRef`), or its
 * key form `<key>/api` for a link somebody keeps (`workAreaPermalinkRef`). `route` is the route as it
 * reads inside the work area: `'/items/F-1?comment=9'`, or segments `['items', key]`.
 *
 * `workAreaRoutePath('acme/tracker')` → `/acme/tracker`;
 * `workAreaRoutePath('acme/tracker', '/items/F-1')` → `/acme/tracker/-/items/F-1`.
 *
 * ⚠️ **A bare reference has no address of its own**, because the first level is always the handle:
 * `/tracker` is a namespace. It is sent to the one address that resolves a bare slug by AC-M18 and
 * shows the candidates when several answer — the interim `/p/tracker/items`, which every link from
 * before the cutover also lands on (AC-AD5). Only a work area whose namespace the server did not send
 * gets one, and no current server omits it (every row is `namespace_id NOT NULL`).
 */
export function workAreaRoutePath(reference: string, route: RouteInputType = ''): string {
  const own = segmentsOf(reference).map(encodeURIComponent);
  const { body, suffix } = routeSuffixOf(route);
  if (own.length === 1) {
    return `/${LEGACY_WORK_AREA_SEGMENT}/${own[0]}${body ? `/${body}` : ''}${suffix}`;
  }
  return `/${own.join('/')}${body ? `/${INTERNAL_ROUTE_SEGMENT}/${body}` : ''}${suffix}`;
}

/** What an address is, read by {@link parseAddress}. Every `route` starts with `/`, or is `''`. */
export type AddressType =
  /** `/` */
  | { readonly kind: 'root' }
  /** `/-/settings` → route `/settings`; `/-` → route `/`. */
  | { readonly kind: 'app'; readonly route: string }
  /** `/login`, `/verify-session/abc`: a door that never moves. */
  | { readonly kind: 'entry'; readonly route: string }
  /** `/api/…`, `/mcp`: answered by the edge, never by the app. */
  | { readonly kind: 'edge'; readonly route: string }
  /** `/acme`, and a namespace's own routes `/acme/-/…` (none yet). */
  | { readonly kind: 'namespace'; readonly namespace: string; readonly route: string }
  /** `/acme/platform/api/-/items/F-1`: the work area, and the route on it (`''` for its home). */
  | {
      readonly kind: 'work-area';
      /** `acme/platform/api`, the form the shared resolver reads. */
      readonly reference: string;
      readonly namespace: string;
      readonly slug: string;
      readonly route: string;
    }
  /** An address from before the cutover: `/p/…`, `/settings`, `/items/…`. See `legacyAddressTarget`. */
  | { readonly kind: 'legacy'; readonly route: string };

/**
 * Reads a pathname (no query, no fragment) into what it addresses.
 *
 * ⛔ **Split at `-`, never at a word.** `/acme/items/tracker/-/items` is work area `tracker` in
 * subgroup `acme/items`: a subgroup may be called anything a route is called, and only the separator
 * says where the work area's path ends (AC-AD1). The first segment decides the rest: `-` is the app,
 * an entry door is a door, an edge segment is the edge's, `p` and the old app segments are legacy,
 * and anything else is a namespace handle, because the first level is always the handle.
 */
export function parseAddress(pathname: string): AddressType {
  const segments = segmentsOf(pathname);
  if (segments.length === 0) return { kind: 'root' };
  const [first, ...rest] = segments;
  const tail = rest.length > 0 ? `/${rest.join('/')}` : '';

  if (first === INTERNAL_ROUTE_SEGMENT) return { kind: 'app', route: tail || '/' };
  if (ROOT_ENTRY_SEGMENTS.includes(first))
    return { kind: 'entry', route: `/${segments.join('/')}` };
  if (EDGE_ROOT_SEGMENTS.includes(first) || first.startsWith('_')) {
    return { kind: 'edge', route: `/${segments.join('/')}` };
  }
  if (first === LEGACY_WORK_AREA_SEGMENT || LEGACY_APP_SEGMENTS.includes(first)) {
    return { kind: 'legacy', route: `/${segments.join('/')}` };
  }

  const separator = segments.indexOf(INTERNAL_ROUTE_SEGMENT);
  const ownPath = separator === -1 ? segments : segments.slice(0, separator);
  const route =
    separator === -1 || separator === segments.length - 1
      ? ''
      : `/${segments.slice(separator + 1).join('/')}`;

  if (ownPath.length === 1) {
    return { kind: 'namespace', namespace: decodeURIComponent(ownPath[0]), route };
  }
  const decoded = ownPath.map((segment) => decodeURIComponent(segment));
  const slug = decoded[decoded.length - 1];
  const namespace = decoded.slice(0, -1).join('/');
  return { kind: 'work-area', reference: decoded.join('/'), namespace, slug, route };
}

/**
 * The route inside the app an address shows, or `null` when it is not an app route. For code that
 * asks "am I on Settings": `appRouteOf(pathname) === '/settings'`.
 */
export function appRouteOf(pathname: string): string | null {
  const address = parseAddress(pathname);
  return address.kind === 'app' ? address.route : null;
}

/**
 * The route a screen shows, inside the app or inside a work area — `/items/F-1` for both
 * `/-/items/F-1` and `/acme/tracker/-/items/F-1`, `''` for a work area's own home — or `null` for any
 * other address. For code that asks "is this the items view", wherever it is.
 */
export function viewRouteOf(pathname: string): string | null {
  const address = parseAddress(pathname);
  return address.kind === 'app' || address.kind === 'work-area' ? address.route : null;
}

/** The work-area reference an address names, `acme/platform/api`, or `null` outside a work area. */
export function workAreaReferenceOf(pathname: string): string | null {
  const address = parseAddress(pathname);
  return address.kind === 'work-area' ? address.reference : null;
}

/** The route parameter names of the work-area patterns: `ns1` … `ns10`, then `workArea`. */
export const WORK_AREA_ROUTE_PARAM = 'workArea';
export function namespaceRouteParam(level: number): string {
  return `ns${level}`;
}

/**
 * The route patterns for one route on a work area, one per namespace depth:
 * `workAreaAddressPatterns('/items/:key')` → `/:ns1/:workArea/-/items/:key`, …,
 * `/:ns1/…/:ns10/:workArea/-/items/:key`. `''` is the work area's own address.
 *
 * `prefix` puts the work-area address inside another route: the decision selected in All work areas
 * is `/-/work-areas/decisions/:ns1/:workArea/:decisionKey`, so it is `prefix` `/-/work-areas/decisions`
 * and `suffix` `/:decisionKey` with `separated` false.
 *
 * ⭐ The web router and the iPhone app's path configuration are both generated from this, so the
 * two can never disagree about which depths exist (AC-AD3, AC-AD10).
 */
export function workAreaAddressPatterns(
  route: string,
  options: { prefix?: string; separated?: boolean } = {},
): string[] {
  const { prefix = '', separated = true } = options;
  const body = segmentsOf(route).join('/');
  const tail = body === '' ? '' : separated ? `/${INTERNAL_ROUTE_SEGMENT}/${body}` : `/${body}`;
  const patterns: string[] = [];
  for (let depth = 1; depth <= MAX_NAMESPACE_DEPTH; depth += 1) {
    const namespaces = Array.from(
      { length: depth },
      (_, index) => `:${namespaceRouteParam(index + 1)}`,
    );
    patterns.push(`${prefix}/${[...namespaces, `:${WORK_AREA_ROUTE_PARAM}`].join('/')}${tail}`);
  }
  return patterns;
}

/**
 * The work-area reference the matched route parameters spell: `{ ns1: 'acme', ns2: 'platform',
 * workArea: 'api' }` → `acme/platform/api`. `undefined` outside a work-area route.
 */
export function workAreaReferenceFromParams(
  params: Readonly<Record<string, string | undefined>>,
): string | undefined {
  const slug = params[WORK_AREA_ROUTE_PARAM];
  if (!slug) return undefined;
  const namespaces: string[] = [];
  for (let level = 1; level <= MAX_NAMESPACE_DEPTH; level += 1) {
    const value = params[namespaceRouteParam(level)];
    if (!value) break;
    namespaces.push(value);
  }
  return [...namespaces, slug].join('/');
}
